/* ==========================================================
THE SIMPLE DATE — BUILD YOUR BOX
UI / PRICING / MILESTONE / PRODUCT DETAIL / MOBILE UX ENGINE
========================================================== */
(() => {
'use strict';

const root = document.querySelector('[data-tsd-build-your-box]');
if (!root) return;

const CONFIG = window.TSD_BUILD_YOUR_BOX || {
  barPrice: 1200,
  checkoutThreshold: 3600,
  freeShippingThreshold: 4000,
  fivePercentThreshold: 6000,
  tenPercentThreshold: 8000,
  subscriptionFirstDiscount: 20,
  subscriptionRecurringDiscount: 12,
  nonFreeShipping: 1000
};

const productCards = Array.from(
  root.querySelectorAll('[data-tsd-product]')
);

const countEl = root.querySelector('[data-tsd-count]');
const currentEl = root.querySelector('[data-tsd-current]');
const nextEl = root.querySelector('[data-tsd-next]');
const progressFill = root.querySelector('[data-tsd-progress-fill]');
const truck = root.querySelector('[data-tsd-truck]');
const selectedContainer = root.querySelector('[data-tsd-selected]');
const subscriptionPriceEl = root.querySelector('[data-tsd-subscription-price]');
const oneTimePriceEl = root.querySelector('[data-tsd-onetime-price]');
const paymentButtons = Array.from(
  root.querySelectorAll('[data-tsd-payment]')
);
const checkoutButton = root.querySelector('[data-tsd-checkout]');
const summaryPanel = root.querySelector('.tsd-summary');
const mobileQuery = window.matchMedia('(max-width: 749px)');

const productModal = root.querySelector('[data-tsd-product-modal]');
const modalDialog = root.querySelector('[data-tsd-modal-dialog]');
const modalContent = root.querySelector('[data-tsd-modal-content]');

const STORAGE_KEY = 'tsd-build-your-box-session-v1';

function readSavedBuilderState() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const saved = JSON.parse(raw);

    if (!saved || !Array.isArray(saved.items)) {
      return null;
    }

    return saved;
  } catch (error) {
    console.warn(
      'TSD builder: could not restore saved box.',
      error
    );
    return null;
  }
}

const savedBuilderState = readSavedBuilderState();

const state = {
  purchaseType:
    savedBuilderState &&
    savedBuilderState.purchaseType === 'onetime'
      ? 'onetime'
      : 'subscription',

  items: new Map(
    savedBuilderState
      ? savedBuilderState.items
          .filter((item) => {
            return (
              item &&
              item.variantId &&
              Number(item.quantity) > 0
            );
          })
          .map((item) => {
            const variantId = String(item.variantId);

            return [
              variantId,
              {
                variantId,
                title: item.title || 'Chocolate Bar',
                price: Number(item.price || CONFIG.barPrice),
                quantity: Math.max(
                  1,
                  Math.floor(Number(item.quantity) || 1)
                )
              }
            ];
          })
      : []
  ),

  mobile: {
    selectedExpanded: false,
    stickyVisible: false
  },

  modal: {
    card: null,
    activeSlide: 0,
    draftQuantity: 0,
    previouslyFocused: null,
    touchStartX: null,
    touchStartY: null
  }
};

function money(cents) {
  const value = Number(cents || 0) / 100;

  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

function saveBuilderState() {
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        purchaseType: state.purchaseType,
        items: Array.from(state.items.values())
      })
    );
  } catch (error) {
    console.warn(
      'TSD builder: could not save box.',
      error
    );
  }
}

function getTotalBars() {
  let count = 0;

  state.items.forEach((item) => {
    count += item.quantity;
  });

  return count;
}

function getMerchandiseSubtotal() {
  let subtotal = 0;

  state.items.forEach((item) => {
    subtotal += item.price * item.quantity;
  });

  return subtotal;
}

function getItem(variantId) {
  return state.items.get(String(variantId));
}

function setItem(card, quantity) {
  if (!card) return;

  const variantId = String(card.dataset.variantId);
  const title = card.dataset.title || 'Chocolate Bar';
  const price = Number(
    card.dataset.price || CONFIG.barPrice
  );

  const safeQuantity = Math.max(
    0,
    Math.floor(Number(quantity) || 0)
  );

  if (safeQuantity === 0) {
    state.items.delete(variantId);
  } else {
    state.items.set(variantId, {
      variantId,
      title,
      price,
      quantity: safeQuantity
    });
  }

  saveBuilderState();
  render();
}

/* ========================================================
   SELECTED BOX — THUMBNAIL / FLAVOUR / QUANTITY
   ======================================================== */

function getCardForVariant(variantId) {
  return productCards.find((card) => {
    return String(card.dataset.variantId) === String(variantId);
  }) || null;
}

function getFlavorLabel(item) {
  const title = String(
    item && item.title ? item.title : ''
  ).trim();

  const normalized = title.toLowerCase();

  const flavorByDateName = [
    [/rich date/, '70% Vegan Dark'],
    [/sweet date/, 'Milk Chocolate'],
    [/nutty date/, 'Toasted Almond'],
    [/pista[-–— ]?show off date/, 'Pistachio'],
    [/peanut better date/, 'Peanut Butter'],
    [/cash[-–— ]?oooh date/, 'Cashew'],
    [/gutsy date/, 'Nuts & Seeds'],
    [/naked date/, 'Plain Medium Dark'],
    [/brazilian nut lift date/, 'Brazil Nut'],
    [/blind date|mystery/, 'Mystery Flavour']
  ];

  const match = flavorByDateName.find(
    ([pattern]) => pattern.test(normalized)
  );

  return match
    ? match[1]
    : title || 'Chocolate Bar';
}

function getItemThumbnail(item) {
  const card = getCardForVariant(item.variantId);

  if (!card) return '';

  const image = card.querySelector('img');

  if (!image) return '';

  return (
    image.currentSrc ||
    image.src ||
    image.getAttribute('src') ||
    ''
  );
}

function isMobile() {
  return mobileQuery.matches;
}

function getMobileStickyCopy(
  retailSubtotal,
  shippingSubtotal,
  bars
) {
  if (retailSubtotal < CONFIG.checkoutThreshold) {
    const barsNeeded = Math.max(0, 3 - bars);

    return {
      status:
        bars === 0
          ? 'START YOUR BOX'
          : `${bars} ${bars === 1 ? 'BAR' : 'BARS'} SELECTED`,

      action:
        barsNeeded === 1
          ? 'ADD 1 MORE TO UNLOCK CHECKOUT →'
          : `ADD ${barsNeeded} MORE TO UNLOCK CHECKOUT →`
    };
  }

  if (shippingSubtotal < CONFIG.freeShippingThreshold) {
    const perBarForShipping =
      state.purchaseType === 'subscription'
        ? Math.round(
            CONFIG.barPrice *
            (
              1 -
              CONFIG.subscriptionFirstDiscount / 100
            )
          )
        : CONFIG.barPrice;

    const barsNeeded = Math.max(
      1,
      Math.ceil(
        (
          CONFIG.freeShippingThreshold -
          shippingSubtotal
        ) /
        perBarForShipping
      )
    );

    return {
      status: 'CHECKOUT UNLOCKED ✓',

      action:
        barsNeeded === 1
          ? 'ADD 1 MORE TO UNLOCK FREE SHIPPING →'
          : `ADD ${barsNeeded} MORE TO UNLOCK FREE SHIPPING →`
    };
  }

  if (state.purchaseType === 'subscription') {
    let action;

    if (bars <= 5) {
      action =
        '20% OFF FIRST ORDER, 12% OFF RECURRING';
    } else if (bars === 6) {
      action =
        'NOW WE’RE GETTING SERIOUS 😏';
    } else if (bars === 7) {
      action =
        'SEVEN DATES. NO COMMITMENT.';
    } else if (bars === 8) {
      action =
        '20% OFF FIRST ORDER · 12% OFF FUTURE ORDERS';
    } else if (bars === 9) {
      action =
        'ONE MORE? YOU’RE THIS CLOSE 😏';
    } else {
      action =
        'YOU CLEARLY HAVE A TYPE. 😏';
    }

    return {
      status: `${bars} DATES IN YOUR BOX ✓`,
      action
    };
  }

  if (retailSubtotal < CONFIG.fivePercentThreshold) {
    const barsNeeded = Math.ceil(
      (
        CONFIG.fivePercentThreshold -
        retailSubtotal
      ) /
      CONFIG.barPrice
    );

    return {
      status:
        'CHECKOUT + FREE SHIPPING UNLOCKED ✓',

      action:
        barsNeeded === 1
          ? 'ADD 1 MORE TO UNLOCK 5% OFF →'
          : `ADD ${barsNeeded} MORE TO UNLOCK 5% OFF →`
    };
  }

  if (retailSubtotal < CONFIG.tenPercentThreshold) {
    const barsNeeded = Math.ceil(
      (
        CONFIG.tenPercentThreshold -
        retailSubtotal
      ) /
      CONFIG.barPrice
    );

    return {
      status:
        '5% ONE-TIME OFF UNLOCKED ✓',

      action:
        barsNeeded === 1
          ? 'ADD 1 MORE TO UNLOCK 10% OFF →'
          : `ADD ${barsNeeded} MORE TO UNLOCK 10% OFF →`
    };
  }

  return {
    status:
      '10% ONE-TIME OFF UNLOCKED ✓',

    action:
      'BEST ONE-TIME PRICE UNLOCKED ✓'
  };
}

function applyBuilderUXPolish() {
  paymentButtons.forEach((button) => {
    const leafTextNodes = Array.from(
      button.querySelectorAll('*')
    ).filter((element) => {
      return (
        element.children.length === 0 &&
        element.textContent &&
        element.textContent.trim()
      );
    });

    const perkNode = leafTextNodes.find(
      (element) => {
        return /20% off your first order|12% off recurring orders|early access|modify or cancel/i
          .test(element.textContent);
      }
    );

    const perkSize = perkNode
      ? window.getComputedStyle(perkNode).fontSize
      : '14px';

    button.style.setProperty(
      '--tsd-option-font-size',
      perkSize
    );
  });

  if (
    !document.getElementById(
      'tsd-builder-ux-polish'
    )
  ) {
    const style =
      document.createElement('style');

    style.id =
      'tsd-builder-ux-polish';

    style.textContent = `
      [data-tsd-build-your-box]
      [data-tsd-payment],
      [data-tsd-build-your-box]
      [data-tsd-payment] * {
        font-size:
          var(
            --tsd-option-font-size,
            14px
          ) !important;
      }

      [data-tsd-build-your-box]
      .tsd-selected__item--editable {
        display: grid;
        grid-template-columns: 48px minmax(0, 1fr);
        gap: 10px;
        align-items: center;
      }

      [data-tsd-build-your-box]
      .tsd-selected__thumb {
        width: 48px;
        height: 48px;
        display: block;
        object-fit: cover;
        border-radius: 8px;
      }

      [data-tsd-build-your-box]
      .tsd-selected__details {
        min-width: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
      }

      [data-tsd-build-your-box]
      .tsd-selected__item--editable
      .tsd-selected__name {
        min-width: 0;
        font-weight: 700;
        line-height: 1.2;
      }

      [data-tsd-build-your-box]
      .tsd-selected__quantity-control {
        flex: 0 0 auto;
        display: inline-grid;
        grid-template-columns: 30px 34px 30px;
        align-items: center;
        overflow: hidden;
        border: 1px solid rgba(41, 37, 47, 0.18);
        border-radius: 999px;
        background: #fff;
      }

      [data-tsd-build-your-box]
      .tsd-selected__quantity-button {
        width: 30px;
        height: 30px;
        padding: 0;
        border: 0;
        background: transparent;
        color: #29252f;
        cursor: pointer;
        font: inherit;
        font-size: 18px;
        line-height: 1;
      }

      [data-tsd-build-your-box]
      .tsd-selected__quantity-button:hover {
        background: rgba(41, 37, 47, 0.05);
      }

      [data-tsd-build-your-box]
      .tsd-selected__quantity-number {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 30px;
        border-left:
          1px solid rgba(41, 37, 47, 0.12);
        border-right:
          1px solid rgba(41, 37, 47, 0.12);
        font-size: 13px;
        font-weight: 700;
        line-height: 1;
      }

      [data-tsd-build-your-box]
      [data-tsd-checkout].is-building {
        cursor: wait !important;
      }

      [data-tsd-build-your-box]
      .tsd-checkout-spinner {
        display: inline-block;
        width: 16px;
        height: 16px;
        margin-right: 9px;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        vertical-align: -3px;
        animation:
          tsd-checkout-spin
          0.7s linear infinite;
      }

      @keyframes tsd-checkout-spin {
        to {
          transform: rotate(360deg);
        }
      }

      @media screen and (max-width: 749px) {
        [data-tsd-build-your-box]
        .tsd-selected__item--editable {
          grid-template-columns:
            44px minmax(0, 1fr);
        }

        [data-tsd-build-your-box]
        .tsd-selected__thumb {
          width: 44px;
          height: 44px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  const introCandidate =
    Array.from(
      root.querySelectorAll('p, div')
    ).find((element) => {
      const text =
        element.textContent
          .replace(/\s+/g, ' ')
          .trim();

      return (
        text ===
          'Pick any flavours. Mix them. Match them. Get more of your favourites.' ||
        text ===
          'Pick any flavours. Mix them. Match them.'
      );
    });

  if (introCandidate) {
    introCandidate.textContent =
      'Pick any flavours. Mix them. Match them. Add bars and your discount applies automatically.';
  }

  if (
    nextEl &&
    !nextEl.dataset.tsdFontReduced
  ) {
    const currentSize =
      parseFloat(
        window
          .getComputedStyle(nextEl)
          .fontSize
      );

    if (
      Number.isFinite(currentSize) &&
      currentSize > 0
    ) {
      nextEl.style.fontSize =
        `${currentSize * (2 / 3)}px`;

      nextEl.dataset.tsdFontReduced =
        'true';
    }
  }
}

function injectMobileStickyStyles() {
  if (
    document.getElementById(
      'tsd-mobile-sticky-styles'
    )
  ) {
    return;
  }

  const style =
    document.createElement('style');

  style.id =
    'tsd-mobile-sticky-styles';

  style.textContent = `
    @media screen and (max-width: 749px) {
      .tsd-mobile-sticky {
        position: fixed;
        z-index: 9990;
        left: 12px;
        right: 12px;
        bottom:
          calc(
            78px +
            env(safe-area-inset-bottom)
          );
        display: grid;
        grid-template-columns:
          minmax(0, 1fr) auto;
        gap: 10px;
        align-items: center;
        padding: 11px 12px;
        border:
          1px solid
          rgba(41, 37, 47, 0.13);
        border-radius: 16px;
        background:
          rgba(255, 255, 255, 0.97);
        color: #29252f;
        box-shadow:
          0 10px 30px
          rgba(41, 37, 47, 0.16);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter:
          blur(10px);
        opacity: 0;
        transform: translateY(14px);
        pointer-events: none;
        transition:
          opacity 160ms ease,
          transform 160ms ease;
      }

      .tsd-mobile-sticky.is-visible {
        opacity: 1;
        transform: translateY(0);
        pointer-events: auto;
      }

      .tsd-mobile-sticky__copy {
        min-width: 0;
      }

      .tsd-mobile-sticky__status {
        display: block;
        margin-bottom: 2px;
        color: #706b73;
        font-size: 9px;
        line-height: 1.15;
        font-weight: 800;
        letter-spacing: 0.045em;
      }

      .tsd-mobile-sticky__action {
        display: block;
        color: #29252f;
        font-size: 12.5px;
        line-height: 1.22;
        font-weight: 800;
      }

      .tsd-mobile-sticky__button {
        min-width: 74px;
        min-height: 38px;
        padding: 8px 10px;
        border: 0;
        border-radius: 999px;
        background: #ff33a7;
        color: #ffffff;
        cursor: pointer;
        font-size: 9.5px;
        line-height: 1.1;
        font-weight: 800;
        letter-spacing: 0.025em;
        white-space: nowrap;
      }

      body.tsd-product-modal-open
      .tsd-mobile-sticky {
        display: none !important;
      }

      .tsd-selected__mobile-toggle {
        width: 100%;
        padding: 11px 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        border: 0;
        border-top:
          1px solid #eee9ef;
        border-bottom:
          1px solid #eee9ef;
        background: transparent;
        color: #29252f;
        cursor: pointer;
        text-align: left;
        font-size: 12px;
        line-height: 1.3;
        font-weight: 750;
      }

      .tsd-selected__mobile-toggle-arrow {
        flex: 0 0 auto;
        color: #706b73;
        font-size: 16px;
        line-height: 1;
      }

      .tsd-selected__mobile-list {
        border-bottom:
          1px solid #eee9ef;
      }

      .tsd-selected__mobile-list
      .tsd-selected__item {
        padding: 8px 0;
      }
    }

    @media screen and (max-width: 390px) {
      .tsd-mobile-sticky {
        left: 8px;
        right: 8px;
        bottom:
          calc(
            74px +
            env(safe-area-inset-bottom)
          );
        padding: 10px;
      }

      .tsd-mobile-sticky__action {
        font-size: 11.5px;
      }

      .tsd-mobile-sticky__button {
        min-width: 68px;
        padding-left: 8px;
        padding-right: 8px;
      }
    }
  `;

  document.head.appendChild(style);
}

function ensureMobileStickyBar() {
  let sticky =
    root.querySelector(
      '[data-tsd-mobile-sticky]'
    );

  if (sticky) return sticky;

  sticky =
    document.createElement('div');

  sticky.className =
    'tsd-mobile-sticky';

  sticky.setAttribute(
    'data-tsd-mobile-sticky',
    ''
  );

  sticky.setAttribute(
    'aria-live',
    'polite'
  );

  sticky.innerHTML = `
    <div class="tsd-mobile-sticky__copy">
      <span
        class="tsd-mobile-sticky__status"
        data-tsd-mobile-sticky-status
      ></span>

      <span
        class="tsd-mobile-sticky__action"
        data-tsd-mobile-sticky-action
      ></span>
    </div>

    <button
      type="button"
      class="tsd-mobile-sticky__button"
      data-tsd-mobile-sticky-button
    >
      VIEW BOX
    </button>
  `;

  root.appendChild(sticky);

  const button =
    sticky.querySelector(
      '[data-tsd-mobile-sticky-button]'
    );

  function goToBox() {
    if (!summaryPanel) return;

    const header =
      document.querySelector(
        'sticky-header, .header-wrapper, .shopify-section-header-sticky'
      );

    const headerHeight =
      header
        ? header
            .getBoundingClientRect()
            .height
        : 0;

    const breathingRoom = 14;

    const targetTop =
      summaryPanel
        .getBoundingClientRect()
        .top +
      window.scrollY -
      headerHeight -
      breathingRoom;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth'
    });
  }

  if (button) {
    button.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
        goToBox();
      }
    );
  }

  sticky.addEventListener(
    'click',
    () => {
      goToBox();
    }
  );

  return sticky;
}

function updateMobileStickyBar(
  retailSubtotal,
  shippingSubtotal,
  bars
) {
  const sticky =
    ensureMobileStickyBar();

  if (!sticky) return;

  const copy =
    getMobileStickyCopy(
      retailSubtotal,
      shippingSubtotal,
      bars
    );

  const statusEl =
    sticky.querySelector(
      '[data-tsd-mobile-sticky-status]'
    );

  const actionEl =
    sticky.querySelector(
      '[data-tsd-mobile-sticky-action]'
    );

  if (statusEl) {
    statusEl.textContent =
      copy.status;
  }

  if (actionEl) {
    actionEl.textContent =
      copy.action;
  }

  sticky.classList.toggle(
    'is-visible',
    Boolean(
      isMobile() &&
      state.mobile.stickyVisible
    )
  );
}

function updateStickyVisibility() {
  const sticky =
    root.querySelector(
      '[data-tsd-mobile-sticky]'
    );

  if (!sticky) return;

  if (!isMobile()) {
    state.mobile.stickyVisible =
      false;

    sticky.classList.remove(
      'is-visible'
    );

    return;
  }

  if (!summaryPanel) {
    state.mobile.stickyVisible =
      true;

    sticky.classList.add(
      'is-visible'
    );

    return;
  }

  const rect =
    summaryPanel.getBoundingClientRect();

  const summaryVisible =
    rect.bottom > 40 &&
    rect.top <
      window.innerHeight - 40;

  state.mobile.stickyVisible =
    !summaryVisible;

  sticky.classList.toggle(
    'is-visible',
    state.mobile.stickyVisible
  );
}

function getOneTimeDiscountPercent(
  subtotal
) {
  if (
    subtotal >=
    CONFIG.tenPercentThreshold
  ) {
    return 10;
  }

  if (
    subtotal >=
    CONFIG.fivePercentThreshold
  ) {
    return 5;
  }

  return 0;
}

function getOneTimePrice(subtotal) {
  const discount =
    getOneTimeDiscountPercent(
      subtotal
    );

  return Math.round(
    subtotal *
    (1 - discount / 100)
  );
}

function getSubscriptionFirstPrice(
  subtotal
) {
  return Math.round(
    subtotal *
    (
      1 -
      CONFIG.subscriptionFirstDiscount /
      100
    )
  );
}

function getMilestoneCopy(
  retailSubtotal,
  shippingSubtotal,
  bars
) {
  if (
    retailSubtotal <
    CONFIG.checkoutThreshold
  ) {
    const barsNeeded =
      Math.max(
        0,
        3 - bars
      );

    return {
      current:
        bars === 0
          ? 'START YOUR BOX'
          : `${bars} ${bars === 1 ? 'BAR' : 'BARS'} SELECTED`,

      next:
        barsNeeded === 1
          ? 'ADD 1 MORE BAR TO UNLOCK CHECKOUT'
          : `ADD ${barsNeeded} MORE BARS TO UNLOCK CHECKOUT`
    };
  }

  if (
    shippingSubtotal <
    CONFIG.freeShippingThreshold
  ) {
    const perBarForShipping =
      state.purchaseType ===
      'subscription'
        ? Math.round(
            CONFIG.barPrice *
            (
              1 -
              CONFIG.subscriptionFirstDiscount /
              100
            )
          )
        : CONFIG.barPrice;

    const barsNeeded =
      Math.max(
        1,
        Math.ceil(
          (
            CONFIG.freeShippingThreshold -
            shippingSubtotal
          ) /
          perBarForShipping
        )
      );

    return {
      current:
        'CHECKOUT UNLOCKED ✓',

      next:
        barsNeeded === 1
          ? 'ADD 1 MORE BAR TO UNLOCK FREE SHIPPING'
          : `ADD ${barsNeeded} MORE BARS TO UNLOCK FREE SHIPPING`
    };
  }

  if (
    state.purchaseType ===
    'subscription'
  ) {
    let nextMessage;

    if (bars <= 5) {
      nextMessage =
        '20% OFF FIRST ORDER, 12% OFF FUTURE ORDERS';

    } else if (bars === 6) {
      nextMessage =
        'NOW WE’RE GETTING SERIOUS 😏';

    } else if (bars === 7) {
      nextMessage =
        'SEVEN DATES. NO COMMITMENT.';

    } else if (bars === 8) {
      nextMessage =
        '20% OFF FIRST ORDER · 12% OFF FUTURE ORDERS';

    } else if (bars === 9) {
      nextMessage =
        'ONE MORE? YOU’RE THIS CLOSE 😏';

    } else {
      nextMessage =
        'YOU CLEARLY HAVE A TYPE. 😏';
    }

    return {
      current:
        `${bars} DATES IN YOUR BOX ✓`,

      next:
        nextMessage
    };
  }

  if (
    retailSubtotal <
    CONFIG.fivePercentThreshold
  ) {
    const barsNeeded =
      Math.ceil(
        (
          CONFIG.fivePercentThreshold -
          retailSubtotal
        ) /
        CONFIG.barPrice
      );

    return {
      current:
        'CHECKOUT + FREE SHIPPING UNLOCKED ✓',

      next:
        barsNeeded === 1
          ? 'ADD 1 MORE BAR TO UNLOCK 5% ONE-TIME OFF'
          : `ADD ${barsNeeded} MORE BARS TO UNLOCK 5% ONE-TIME OFF`
    };
  }

  if (
    retailSubtotal <
    CONFIG.tenPercentThreshold
  ) {
    const barsNeeded =
      Math.ceil(
        (
          CONFIG.tenPercentThreshold -
          retailSubtotal
        ) /
        CONFIG.barPrice
      );

    return {
      current:
        'CHECKOUT + FREE SHIPPING + 5% ONE-TIME OFF UNLOCKED ✓',

      next:
        barsNeeded === 1
          ? 'ADD 1 MORE BAR TO UNLOCK 10% ONE-TIME OFF'
          : `ADD ${barsNeeded} MORE BARS TO UNLOCK 10% ONE-TIME OFF`
    };
  }

  return {
    current:
      'CHECKOUT + FREE SHIPPING + 10% ONE-TIME OFF UNLOCKED ✓',

    next:
      'BEST ONE-TIME PRICING UNLOCKED ✓'
  };
}

function getVisualProgress(
  retailSubtotal,
  shippingSubtotal
) {
  if (retailSubtotal <= 0) {
    return 0;
  }

  if (
    retailSubtotal <
    CONFIG.checkoutThreshold
  ) {
    return (
      retailSubtotal /
      CONFIG.checkoutThreshold
    ) * 20;
  }

  if (
    shippingSubtotal <
    CONFIG.freeShippingThreshold
  ) {
    const checkoutShippingValue =
      state.purchaseType ===
      'subscription'
        ? Math.round(
            CONFIG.checkoutThreshold *
            (
              1 -
              CONFIG.subscriptionFirstDiscount /
              100
            )
          )
        : CONFIG.checkoutThreshold;

    const range =
      CONFIG.freeShippingThreshold -
      checkoutShippingValue;

    const progress =
      shippingSubtotal -
      checkoutShippingValue;

    const ratio =
      range > 0
        ? Math.max(
            0,
            Math.min(
              1,
              progress / range
            )
          )
        : 1;

    return 20 + ratio * 20;
  }

  if (
    retailSubtotal <
    CONFIG.fivePercentThreshold
  ) {
    const range =
      CONFIG.fivePercentThreshold -
      CONFIG.freeShippingThreshold;

    const progress =
      retailSubtotal -
      CONFIG.freeShippingThreshold;

    return (
      40 +
      (progress / range) * 20
    );
  }

  if (
    retailSubtotal <
    CONFIG.tenPercentThreshold
  ) {
    const range =
      CONFIG.tenPercentThreshold -
      CONFIG.fivePercentThreshold;

    const progress =
      retailSubtotal -
      CONFIG.fivePercentThreshold;

    return (
      60 +
      (progress / range) * 20
    );
  }

  const tenBarSubtotal =
    CONFIG.barPrice * 10;

  if (
    retailSubtotal <
    tenBarSubtotal
  ) {
    const range =
      tenBarSubtotal -
      CONFIG.tenPercentThreshold;

    const progress =
      retailSubtotal -
      CONFIG.tenPercentThreshold;

    return (
      80 +
      (progress / range) * 15
    );
  }

  return 95;
}

function updateDots(
  retailSubtotal,
  shippingSubtotal
) {
  root
    .querySelectorAll(
      '[data-tsd-dot]'
    )
    .forEach((dot) => {
      const threshold =
        Number(
          dot.dataset.tsdDot
        ) * 100;

      let isHit = false;

      if (
        threshold ===
        CONFIG.checkoutThreshold
      ) {
        isHit =
          retailSubtotal >=
          CONFIG.checkoutThreshold;

      } else if (
        threshold ===
        CONFIG.freeShippingThreshold
      ) {
        isHit =
          shippingSubtotal >=
          CONFIG.freeShippingThreshold;

      } else if (
        state.purchaseType ===
        'onetime'
      ) {
        isHit =
          retailSubtotal >= threshold;
      }

      dot.classList.toggle(
        'is-hit',
        isHit
      );
    });
}

function updateProductCards() {
  productCards.forEach(
    (card) => {
      const variantId =
        String(
          card.dataset.variantId
        );

      const item =
        getItem(variantId);

      const quantityEl =
        card.querySelector(
          '[data-tsd-quantity]'
        );

      if (quantityEl) {
        quantityEl.textContent =
          item
            ? item.quantity
            : 0;
      }

      card.classList.toggle(
        'has-selection',
        Boolean(
          item &&
          item.quantity > 0
        )
      );
    }
  );
}

/* ========================================================
   SELECTED DATES — EDITABLE RIGHT-HAND PANEL
   ======================================================== */

function appendSelectedRows(
  container
) {
  state.items.forEach(
    (item) => {
      const row =
        document.createElement(
          'div'
        );

      row.className =
        'tsd-selected__item tsd-selected__item--editable';

      const thumbnail =
        getItemThumbnail(item);

      const flavor =
        getFlavorLabel(item);

      row.innerHTML = `
        ${thumbnail ? `
          <img
            class="tsd-selected__thumb"
            src="${escapeHtml(thumbnail)}"
            alt=""
            loading="lazy"
          >
        ` : ''}

        <div class="tsd-selected__details">
          <div class="tsd-selected__name">
            ${escapeHtml(flavor)}
          </div>

          <div
            class="tsd-selected__quantity-control"
            aria-label="Quantity for ${escapeHtml(flavor)}"
          >
            <button
              type="button"
              class="tsd-selected__quantity-button"
              data-tsd-selected-minus
              aria-label="Remove one ${escapeHtml(flavor)}"
            >−</button>

            <span
              class="tsd-selected__quantity-number"
              aria-live="polite"
            >${item.quantity}</span>

            <button
              type="button"
              class="tsd-selected__quantity-button"
              data-tsd-selected-plus
              aria-label="Add one ${escapeHtml(flavor)}"
            >+</button>
          </div>
        </div>
      `;

      const minus =
        row.querySelector(
          '[data-tsd-selected-minus]'
        );

      const plus =
        row.querySelector(
          '[data-tsd-selected-plus]'
        );

      if (minus) {
        minus.addEventListener(
          'click',
          () => {
            const card =
              getCardForVariant(
                item.variantId
              );

            if (!card) return;

            setItem(
              card,
              item.quantity - 1
            );
          }
        );
      }

      if (plus) {
        plus.addEventListener(
          'click',
          () => {
            const card =
              getCardForVariant(
                item.variantId
              );

            if (!card) return;

            setItem(
              card,
              item.quantity + 1
            );
          }
        );
      }

      container.appendChild(row);
    }
  );
}

function renderSelectedItems() {
  if (!selectedContainer) {
    return;
  }

  selectedContainer.innerHTML = '';

  const bars =
    getTotalBars();

  if (
    state.items.size === 0
  ) {
    state.mobile.selectedExpanded =
      false;

    selectedContainer.innerHTML = `
      <div class="tsd-selected__empty">
        Your Dates will appear here as you add them.
      </div>
    `;

    return;
  }

  if (isMobile()) {
    const toggle =
      document.createElement(
        'button'
      );

    toggle.type = 'button';

    toggle.className =
      'tsd-selected__mobile-toggle';

    toggle.setAttribute(
      'aria-expanded',
      state.mobile.selectedExpanded
        ? 'true'
        : 'false'
    );

    toggle.innerHTML = `
      <span>
        ${bars}
        ${bars === 1 ? 'bar' : 'bars'}
        selected ·
        ${
          state.mobile.selectedExpanded
            ? 'Hide your Dates'
            : 'View your Dates'
        }
      </span>

      <span
        class="tsd-selected__mobile-toggle-arrow"
        aria-hidden="true"
      >
        ${
          state.mobile.selectedExpanded
            ? '⌃'
            : '›'
        }
      </span>
    `;

    toggle.addEventListener(
      'click',
      () => {
        state.mobile.selectedExpanded =
          !state.mobile.selectedExpanded;

        renderSelectedItems();
      }
    );

    selectedContainer.appendChild(
      toggle
    );

    if (
      state.mobile.selectedExpanded
    ) {
      const list =
        document.createElement(
          'div'
        );

      list.className =
        'tsd-selected__mobile-list';

      appendSelectedRows(list);

      selectedContainer.appendChild(
        list
      );
    }

    return;
  }

  appendSelectedRows(
    selectedContainer
  );
}

function updatePaymentSelection() {
  paymentButtons.forEach(
    (button) => {
      const type =
        button.dataset.tsdPayment;

      const selected =
        type ===
        state.purchaseType;

      button.classList.toggle(
        'is-selected',
        selected
      );

      button.setAttribute(
        'aria-pressed',
        selected
          ? 'true'
          : 'false'
      );
    }
  );
}

function updateCheckoutButton(
  subtotal,
  bars,
  subscriptionPrice,
  oneTimePrice
) {
  if (!checkoutButton) {
    return;
  }

  if (
    subtotal <
    CONFIG.checkoutThreshold
  ) {
    checkoutButton.disabled = true;

    const barsNeeded =
      Math.max(
        0,
        3 - bars
      );

    if (barsNeeded === 1) {
      checkoutButton.textContent =
        'ADD 1 MORE BAR TO CHECK OUT';
    } else {
      checkoutButton.textContent =
        `ADD ${barsNeeded} MORE BARS TO CHECK OUT`;
    }

    return;
  }

  checkoutButton.disabled = false;

  const selectedPrice =
    state.purchaseType ===
    'subscription'
      ? subscriptionPrice
      : oneTimePrice;

  checkoutButton.textContent =
    `CHECKOUT — ${money(selectedPrice)}`;
}

function render() {
  const bars =
    getTotalBars();

  const subtotal =
    getMerchandiseSubtotal();

  const oneTimePrice =
    getOneTimePrice(
      subtotal
    );

  const subscriptionPrice =
    getSubscriptionFirstPrice(
      subtotal
    );

  const shippingSubtotal =
    state.purchaseType ===
    'subscription'
      ? subscriptionPrice
      : subtotal;

  const milestone =
    getMilestoneCopy(
      subtotal,
      shippingSubtotal,
      bars
    );

  const visualProgress =
    getVisualProgress(
      subtotal,
      shippingSubtotal
    );

  if (countEl) {
    countEl.textContent =
      bars;
  }

  if (currentEl) {
    currentEl.textContent =
      milestone.current;
  }

  if (nextEl) {
    nextEl.textContent =
      milestone.next;
  }

  if (progressFill) {
    progressFill.style.width =
      `${visualProgress}%`;
  }

  if (truck) {
    truck.style.left =
      `${visualProgress}%`;

    truck.style.transform =
      'translateX(-50%) scaleX(-1)';
  }

  if (subscriptionPriceEl) {
    subscriptionPriceEl.textContent =
      money(
        subscriptionPrice
      );
  }

  if (oneTimePriceEl) {
    oneTimePriceEl.textContent =
      money(
        oneTimePrice
      );
  }

  updateDots(
    subtotal,
    shippingSubtotal
  );

  updateProductCards();
  renderSelectedItems();
  updatePaymentSelection();

  updateCheckoutButton(
    subtotal,
    bars,
    subscriptionPrice,
    oneTimePrice
  );

  updateMobileStickyBar(
    subtotal,
    shippingSubtotal,
    bars
  );

  window.requestAnimationFrame(
    () => {
      updateStickyVisibility();
    }
  );
}

function isModalOpen() {
  return Boolean(
    productModal &&
    productModal.classList.contains(
      'is-open'
    )
  );
}

function getModalSlides() {
  if (!modalContent) {
    return [];
  }

  return Array.from(
    modalContent.querySelectorAll(
      '[data-tsd-modal-slide]'
    )
  );
}

function getModalThumbnails() {
  if (!modalContent) {
    return [];
  }

  return Array.from(
    modalContent.querySelectorAll(
      '[data-tsd-modal-thumbnail]'
    )
  );
}

function getModalDots() {
  if (!modalContent) {
    return [];
  }

  return Array.from(
    modalContent.querySelectorAll(
      '[data-tsd-modal-dot]'
    )
  );
}

function getModalQuantityEl() {
  if (!modalContent) {
    return null;
  }

  return modalContent.querySelector(
    '[data-tsd-modal-quantity]'
  );
}

function updateModalQuantityDisplay() {
  const quantityEl =
    getModalQuantityEl();

  if (quantityEl) {
    quantityEl.textContent =
      state.modal.draftQuantity;
  }
}

function showModalSlide(index) {
  const slides =
    getModalSlides();

  if (!slides.length) {
    return;
  }

  let safeIndex =
    Number(index);

  if (
    Number.isNaN(safeIndex) ||
    safeIndex < 0
  ) {
    safeIndex =
      slides.length - 1;
  }

  if (
    safeIndex >= slides.length
  ) {
    safeIndex = 0;
  }

  state.modal.activeSlide =
    safeIndex;

  slides.forEach(
    (slide, slideIndex) => {
      slide.classList.toggle(
        'is-active',
        slideIndex === safeIndex
      );
    }
  );

  getModalThumbnails()
    .forEach(
      (thumbnail) => {
        const thumbnailIndex =
          Number(
            thumbnail.dataset
              .slideIndex
          );

        thumbnail.classList.toggle(
          'is-active',
          thumbnailIndex ===
          safeIndex
        );
      }
    );

  getModalDots()
    .forEach(
      (dot) => {
        const dotIndex =
          Number(
            dot.dataset
              .slideIndex
          );

        dot.classList.toggle(
          'is-active',
          dotIndex ===
          safeIndex
        );
      }
    );
}

function showPreviousModalSlide() {
  showModalSlide(
    state.modal.activeSlide - 1
  );
}

function showNextModalSlide() {
  showModalSlide(
    state.modal.activeSlide + 1
  );
}

function openProductModal(
  card,
  trigger
) {
  if (
    !productModal ||
    !modalContent ||
    !card
  ) {
    return;
  }

  const template =
    card.querySelector(
      '[data-tsd-product-details-template]'
    );

  if (!template) {
    return;
  }

  state.modal.previouslyFocused =
    trigger ||
    document.activeElement;

  state.modal.card = card;
  state.modal.activeSlide = 0;

  state.modal.draftQuantity = 1;

  modalContent.innerHTML = '';

  modalContent.appendChild(
    template.content.cloneNode(
      true
    )
  );

  showModalSlide(0);
  updateModalQuantityDisplay();

  productModal.classList.add(
    'is-open'
  );

  productModal.setAttribute(
    'aria-hidden',
    'false'
  );

  document.body.classList.add(
    'tsd-product-modal-open'
  );

  modalContent.scrollTop = 0;

  window.requestAnimationFrame(
    () => {
      const closeButton =
        productModal.querySelector(
          '.tsd-product-modal__close'
        );

      if (closeButton) {
        closeButton.focus({
          preventScroll: true
        });
      }
    }
  );
}

function closeProductModal() {
  if (!productModal) {
    return;
  }

  productModal.classList.remove(
    'is-open'
  );

  productModal.setAttribute(
    'aria-hidden',
    'true'
  );

  document.body.classList.remove(
    'tsd-product-modal-open'
  );

  state.modal.card = null;
  state.modal.activeSlide = 0;
  state.modal.draftQuantity = 0;
  state.modal.touchStartX = null;
  state.modal.touchStartY = null;

  const previous =
    state.modal.previouslyFocused;

  state.modal.previouslyFocused =
    null;

  if (
    previous &&
    typeof previous.focus ===
    'function'
  ) {
    window.requestAnimationFrame(
      () => {
        previous.focus({
          preventScroll: true
        });
      }
    );
  }

  window.setTimeout(
    () => {
      if (
        modalContent &&
        !isModalOpen()
      ) {
        modalContent.innerHTML = '';
      }
    },
    200
  );
}

function changeModalDraftQuantity(
  change
) {
  const nextQuantity =
    Math.max(
      0,
      state.modal.draftQuantity +
      change
    );

  state.modal.draftQuantity =
    nextQuantity;

  updateModalQuantityDisplay();
}

function commitModalQuantity() {
  if (!state.modal.card) {
    return;
  }

  const variantId =
    String(
      state.modal.card.dataset.variantId
    );

  const existingItem =
    getItem(variantId);

  const existingQuantity =
    existingItem
      ? existingItem.quantity
      : 0;

  const quantityToAdd =
    Math.max(
      0,
      Math.floor(
        Number(
          state.modal.draftQuantity
        ) || 0
      )
    );

  if (quantityToAdd === 0) {
    return;
  }

  setItem(
    state.modal.card,
    existingQuantity +
      quantityToAdd
  );

  updateModalQuantityDisplay();

  const addButton =
    modalContent
      ? modalContent.querySelector(
          '[data-tsd-modal-add]'
        )
      : null;

  if (addButton) {
    const originalText =
      addButton.textContent;

    addButton.textContent =
      'ADDED ✓';

    window.setTimeout(
      () => {
        if (
          addButton &&
          document.body.contains(
            addButton
          )
        ) {
          addButton.textContent =
            originalText;
        }
      },
      900
    );
  }
}

if (productModal) {
  productModal.addEventListener(
    'click',
    (event) => {
      const closeTrigger =
        event.target.closest(
          '[data-tsd-modal-close]'
        );

      if (closeTrigger) {
        closeProductModal();
        return;
      }

      const previousButton =
        event.target.closest(
          '[data-tsd-modal-prev]'
        );

      if (previousButton) {
        showPreviousModalSlide();
        return;
      }

      const nextButton =
        event.target.closest(
          '[data-tsd-modal-next]'
        );

      if (nextButton) {
        showNextModalSlide();
        return;
      }

      const thumbnail =
        event.target.closest(
          '[data-tsd-modal-thumbnail]'
        );

      if (thumbnail) {
        showModalSlide(
          Number(
            thumbnail.dataset
              .slideIndex
          )
        );

        return;
      }

      const dot =
        event.target.closest(
          '[data-tsd-modal-dot]'
        );

      if (dot) {
        showModalSlide(
          Number(
            dot.dataset
              .slideIndex
          )
        );

        return;
      }

      const modalMinus =
        event.target.closest(
          '[data-tsd-modal-minus]'
        );

      if (modalMinus) {
        changeModalDraftQuantity(-1);
        return;
      }

      const modalPlus =
        event.target.closest(
          '[data-tsd-modal-plus]'
        );

      if (modalPlus) {
        changeModalDraftQuantity(1);
        return;
      }

      const modalAdd =
        event.target.closest(
          '[data-tsd-modal-add]'
        );

      if (modalAdd) {
        commitModalQuantity();
      }
    }
  );
}

if (modalContent) {
  modalContent.addEventListener(
    'touchstart',
    (event) => {
      const stage =
        event.target.closest(
          '[data-tsd-modal-stage]'
        );

      if (
        !stage ||
        !event.touches ||
        event.touches.length !== 1
      ) {
        return;
      }

      state.modal.touchStartX =
        event.touches[0].clientX;

      state.modal.touchStartY =
        event.touches[0].clientY;
    },
    {
      passive: true
    }
  );

  modalContent.addEventListener(
    'touchend',
    (event) => {
      if (
        state.modal.touchStartX === null ||
        state.modal.touchStartY === null ||
        !event.changedTouches ||
        !event.changedTouches.length
      ) {
        return;
      }

      const endX =
        event.changedTouches[0]
          .clientX;

      const endY =
        event.changedTouches[0]
          .clientY;

      const deltaX =
        endX -
        state.modal.touchStartX;

      const deltaY =
        endY -
        state.modal.touchStartY;

      state.modal.touchStartX = null;
      state.modal.touchStartY = null;

      if (
        Math.abs(deltaX) < 45 ||
        Math.abs(deltaX) <=
          Math.abs(deltaY)
      ) {
        return;
      }

      if (deltaX < 0) {
        showNextModalSlide();
      } else {
        showPreviousModalSlide();
      }
    },
    {
      passive: true
    }
  );
}

document.addEventListener(
  'keydown',
  (event) => {
    if (!isModalOpen()) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeProductModal();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showPreviousModalSlide();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      showNextModalSlide();
    }
  }
);

productCards.forEach(
  (card) => {
    const plusButton =
      card.querySelector(
        '[data-tsd-plus]'
      );

    const minusButton =
      card.querySelector(
        '[data-tsd-minus]'
      );

    const detailTriggers =
      Array.from(
        card.querySelectorAll(
          '[data-tsd-open-details]'
        )
      );

    if (plusButton) {
      plusButton.addEventListener(
        'click',
        () => {
          const variantId =
            String(
              card.dataset.variantId
            );

          const current =
            getItem(variantId);

          const quantity =
            current
              ? current.quantity + 1
              : 1;

          setItem(
            card,
            quantity
          );
        }
      );
    }

    if (minusButton) {
      minusButton.addEventListener(
        'click',
        () => {
          const variantId =
            String(
              card.dataset.variantId
            );

          const current =
            getItem(variantId);

          if (!current) {
            return;
          }

          setItem(
            card,
            current.quantity - 1
          );
        }
      );
    }

    detailTriggers.forEach(
      (trigger) => {
        trigger.addEventListener(
          'click',
          () => {
            openProductModal(
              card,
              trigger
            );
          }
        );
      }
    );
  }
);

paymentButtons.forEach(
  (button) => {
    button.addEventListener(
      'click',
      () => {
        const type =
          button.dataset
            .tsdPayment;

        if (
          type !== 'subscription' &&
          type !== 'onetime'
        ) {
          return;
        }

        state.purchaseType = type;

        saveBuilderState();
        render();
      }
    );
  }
);

function refreshMobileUX() {
  if (!isMobile()) {
    state.mobile.selectedExpanded =
      false;
  }

  render();
  updateStickyVisibility();
}

window.addEventListener(
  'scroll',
  updateStickyVisibility,
  {
    passive: true
  }
);

window.addEventListener(
  'resize',
  updateStickyVisibility
);

if (
  typeof mobileQuery.addEventListener ===
  'function'
) {
  mobileQuery.addEventListener(
    'change',
    refreshMobileUX
  );

} else if (
  typeof mobileQuery.addListener ===
  'function'
) {
  mobileQuery.addListener(
    refreshMobileUX
  );
}

/* ========================================================
   REAL SHOPIFY / SEAL CHECKOUT
   ======================================================== */

if (checkoutButton) {
  checkoutButton.addEventListener(
    'click',
    async (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      const subtotal =
        getMerchandiseSubtotal();

      if (
        subtotal <
        CONFIG.checkoutThreshold
      ) {
        return;
      }

      const selectedItems =
        Array.from(
          state.items.values()
        );

      if (!selectedItems.length) {
        return;
      }

      const originalButtonText =
        checkoutButton.textContent;

      saveBuilderState();

      /*
        NEW LOADING STATE:
        spinner + BUILDING YOUR BOX
      */

      checkoutButton.disabled = true;

      checkoutButton.classList.add(
        'is-building'
      );

      checkoutButton.setAttribute(
        'aria-busy',
        'true'
      );

      checkoutButton.innerHTML =
        '<span class="tsd-checkout-spinner" aria-hidden="true"></span>' +
        '<span>BUILDING YOUR BOX</span>';

      try {
        const cartItems =
          selectedItems.map(
            (item) => {
              const cartItem = {
                id:
                  Number(
                    item.variantId
                  ),

                quantity:
                  Number(
                    item.quantity
                  )
              };

              if (
                state.purchaseType ===
                'subscription'
              ) {
                cartItem.selling_plan =
                  2962784427;
              }

              return cartItem;
            }
          );

        const shopRoot =
          (
            window.Shopify &&
            window.Shopify.routes &&
            window.Shopify.routes.root
          )
            ? window.Shopify.routes.root
            : '/';

        const clearResponse =
          await fetch(
            `${shopRoot}cart/clear.js`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                'Accept':
                  'application/json'
              }
            }
          );

        if (!clearResponse.ok) {
          const clearError =
            await clearResponse.text();

          throw new Error(
            `Could not clear cart (${clearResponse.status}): ${clearError}`
          );
        }

        const addResponse =
          await fetch(
            `${shopRoot}cart/add.js`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                'Accept':
                  'application/json'
              },

              body:
                JSON.stringify({
                  items: cartItems
                })
            }
          );

        if (!addResponse.ok) {
          const addError =
            await addResponse.text();

          throw new Error(
            `Could not add products (${addResponse.status}): ${addError}`
          );
        }

        const checkoutForm =
          document.createElement(
            'form'
          );

        checkoutForm.method =
          'POST';

        checkoutForm.action =
          `${shopRoot}cart`;

        const checkoutInput =
          document.createElement(
            'input'
          );

        checkoutInput.type =
          'hidden';

        checkoutInput.name =
          'checkout';

        checkoutInput.value =
          'Check out';

        checkoutForm.appendChild(
          checkoutInput
        );

        document.body.appendChild(
          checkoutForm
        );

        checkoutForm.submit();

      } catch (error) {
        console.error(
          'TSD checkout error:',
          error
        );

        alert(
          'We could not start checkout.\n\n' +
          (
            error &&
            error.message
              ? error.message
              : String(error)
          )
        );

        checkoutButton.disabled =
          false;

        checkoutButton.classList.remove(
          'is-building'
        );

        checkoutButton.removeAttribute(
          'aria-busy'
        );

        checkoutButton.textContent =
          originalButtonText;
      }
    }
  );
}

function escapeHtml(value) {
  return String(value)
    .replace(
      /&/g,
      '&amp;'
    )
    .replace(
      /</g,
      '&lt;'
    )
    .replace(
      />/g,
      '&gt;'
    )
    .replace(
      /"/g,
      '&quot;'
    )
    .replace(
      /'/g,
      '&#039;'
    );
}

injectMobileStickyStyles();
applyBuilderUXPolish();
ensureMobileStickyBar();

saveBuilderState();
render();

window.addEventListener(
  'pageshow',
  () => {
    render();
  }
);

window.requestAnimationFrame(
  () => {
    updateStickyVisibility();
  }
);

})();
/* ==========================================================
   BYB — SUBSCRIPTION SAVINGS CLARITY
   ========================================================== */

(() => {
  'use strict';

  if (window.__TSD_BYB_SAVINGS_CLARITY__) return;
  window.__TSD_BYB_SAVINGS_CLARITY__ = true;

  const root =
    document.querySelector(
      '[data-tsd-build-your-box]'
    );

  if (!root) return;

  const fiveLabel =
    root.querySelector(
      '.tsd-progress__label--60 span'
    );

  const tenLabel =
    root.querySelector(
      '.tsd-progress__label--80 span'
    );

  const fiveMilestone =
    root.querySelector(
      '.tsd-progress__label--60'
    );

  const tenMilestone =
    root.querySelector(
      '.tsd-progress__label--80'
    );

  const fiveDot =
    root.querySelector(
      '.tsd-progress__dot--five'
    );

  const tenDot =
    root.querySelector(
      '.tsd-progress__dot--ten'
    );

  const progress =
    root.querySelector(
      '.tsd-progress'
    );

  const style =
    document.createElement('style');

  style.textContent = `
    .tsd-one-time-milestone-blur {
      filter: blur(1.15px);
      opacity: 1 !important;

      transition:
        filter 180ms ease;
    }

    .tsd-subscription-savings-active {
      margin: 4px 0 14px;
      padding: 9px 11px;

      border-radius: 10px;

      background: #fff4fa;
      color: #29252f;

      font-size: 11px;
      line-height: 1.4;
      font-weight: 800;
      letter-spacing: 0.015em;
    }

    .tsd-subscription-savings-active[hidden] {
      display: none !important;
    }

    @media screen and (max-width: 749px) {
      .tsd-subscription-savings-active {
        margin: 3px 0 13px;
        padding: 8px 10px;

        font-size: 11px;
        line-height: 1.35;
      }
    }
  `;

  document.head.appendChild(style);

  let note =
    root.querySelector(
      '[data-tsd-subscription-savings-active]'
    );

  if (!note && progress) {
    note =
      document.createElement('p');

    note.className =
      'tsd-subscription-savings-active';

    note.setAttribute(
      'data-tsd-subscription-savings-active',
      ''
    );

    note.textContent =
      'SUBSCRIPTION SAVINGS ACTIVE: 20% OFF YOUR FIRST ORDER · 12% OFF FUTURE ORDERS ✓';

    progress.insertAdjacentElement(
      'afterend',
      note
    );
  }

  function updateSavingsDisplay() {
    const selected =
      root.querySelector(
        '[data-tsd-payment].is-selected'
      );

    const subscriptionSelected =
      Boolean(
        selected &&
        selected.dataset.tsdPayment ===
          'subscription'
      );

    if (fiveLabel) {
      fiveLabel.textContent =
        subscriptionSelected
          ? '5% ONE-TIME'
          : '5% OFF';
    }

    if (tenLabel) {
      tenLabel.textContent =
        subscriptionSelected
          ? '10% ONE-TIME'
          : '10% OFF';
    }

    [
      fiveMilestone,
      tenMilestone,
      fiveDot,
      tenDot
    ].forEach((element) => {
      if (!element) return;

      element.classList.toggle(
        'tsd-one-time-milestone-blur',
        subscriptionSelected
      );
    });

    if (note) {
      note.hidden =
        !subscriptionSelected;
    }
  }

  root
    .querySelectorAll(
      '[data-tsd-payment]'
    )
    .forEach((button) => {
      new MutationObserver(
        updateSavingsDisplay
      ).observe(
        button,
        {
          attributes: true,
          attributeFilter: [
            'class',
            'aria-pressed'
          ]
        }
      );
    });

  root.addEventListener(
    'click',
    (event) => {
      if (
        event.target.closest(
          '[data-tsd-payment]'
        )
      ) {
        window.requestAnimationFrame(
          updateSavingsDisplay
        );
      }
    }
  );

  updateSavingsDisplay();
})();